/**
 * Frontend caching layer with TTL support
 * Uses memory cache with optional localStorage persistence
 */

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

export interface CacheOptions {
  ttl?: number; // Default TTL in milliseconds (default: 5 minutes)
  useLocalStorage?: boolean; // Persist to localStorage (default: false)
  localStoragePrefix?: string; // Prefix for localStorage keys (default: 'cache_')
}

const DEFAULT_OPTIONS: Required<CacheOptions> = {
  ttl: 5 * 60 * 1000, // 5 minutes
  useLocalStorage: false,
  localStoragePrefix: 'cache_',
};

class CacheManager {
  private memoryCache: Map<string, CacheEntry<any>> = new Map();
  private options: Required<CacheOptions>;

  constructor(options: CacheOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.loadFromLocalStorage();
  }

  /**
   * Get value from cache
   */
  get<T>(key: string): T | null {
    const entry = this.memoryCache.get(key);

    if (!entry) {
      // Try loading from localStorage if enabled
      if (this.options.useLocalStorage) {
        return this.getFromLocalStorage<T>(key);
      }
      return null;
    }

    // Check if entry has expired
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.memoryCache.delete(key);
      if (this.options.useLocalStorage) {
        this.removeFromLocalStorage(key);
      }
      return null;
    }

    return entry.data as T;
  }

  /**
   * Set value in cache
   */
  set<T>(key: string, value: T, ttl?: number): void {
    const entry: CacheEntry<T> = {
      data: value,
      timestamp: Date.now(),
      ttl: ttl || this.options.ttl,
    };

    this.memoryCache.set(key, entry);

    if (this.options.useLocalStorage) {
      this.saveToLocalStorage(key, entry);
    }
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    const entry = this.memoryCache.get(key);
    if (!entry) {
      if (this.options.useLocalStorage) {
        return this.hasInLocalStorage(key);
      }
      return false;
    }

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.memoryCache.delete(key);
      if (this.options.useLocalStorage) {
        this.removeFromLocalStorage(key);
      }
      return false;
    }

    return true;
  }

  /**
   * Delete value from cache
   */
  delete(key: string): void {
    this.memoryCache.delete(key);
    if (this.options.useLocalStorage) {
      this.removeFromLocalStorage(key);
    }
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.memoryCache.clear();
    if (this.options.useLocalStorage) {
      this.clearLocalStorage();
    }
  }

  /**
   * Clear expired entries
   */
  clearExpired(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.memoryCache.forEach((entry, key) => {
      if (now - entry.timestamp > entry.ttl) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => {
      this.memoryCache.delete(key);
      if (this.options.useLocalStorage) {
        this.removeFromLocalStorage(key);
      }
    });
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    keys: string[];
  } {
    return {
      size: this.memoryCache.size,
      keys: Array.from(this.memoryCache.keys()),
    };
  }

  // LocalStorage methods
  private getLocalStorageKey(key: string): string {
    return `${this.options.localStoragePrefix}${key}`;
  }

  private getFromLocalStorage<T>(key: string): T | null {
    try {
      const storageKey = this.getLocalStorageKey(key);
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      const entry: CacheEntry<T> = JSON.parse(stored);
      const now = Date.now();

      if (now - entry.timestamp > entry.ttl) {
        this.removeFromLocalStorage(key);
        return null;
      }

      // Also add to memory cache for faster access
      this.memoryCache.set(key, entry);
      return entry.data;
    } catch {
      return null;
    }
  }

  private saveToLocalStorage<T>(key: string, entry: CacheEntry<T>): void {
    try {
      const storageKey = this.getLocalStorageKey(key);
      localStorage.setItem(storageKey, JSON.stringify(entry));
    } catch (error) {
      // localStorage might be full or disabled
      console.warn('Failed to save to localStorage:', error);
    }
  }

  private removeFromLocalStorage(key: string): void {
    try {
      const storageKey = this.getLocalStorageKey(key);
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore errors
    }
  }

  private hasInLocalStorage(key: string): boolean {
    try {
      const storageKey = this.getLocalStorageKey(key);
      return localStorage.getItem(storageKey) !== null;
    } catch {
      return false;
    }
  }

  private loadFromLocalStorage(): void {
    if (!this.options.useLocalStorage || typeof window === 'undefined') {
      return;
    }

    try {
      const prefix = this.options.localStoragePrefix;
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(prefix));

      cacheKeys.forEach(storageKey => {
        const key = storageKey.replace(prefix, '');
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          try {
            const entry: CacheEntry<any> = JSON.parse(stored);
            const now = Date.now();

            // Only load non-expired entries
            if (now - entry.timestamp <= entry.ttl) {
              this.memoryCache.set(key, entry);
            } else {
              // Remove expired entries
              localStorage.removeItem(storageKey);
            }
          } catch {
            // Invalid entry, remove it
            localStorage.removeItem(storageKey);
          }
        }
      });
    } catch (error) {
      console.warn('Failed to load from localStorage:', error);
    }
  }

  private clearLocalStorage(): void {
    if (typeof window === 'undefined') return;

    try {
      const prefix = this.options.localStoragePrefix;
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(prefix));

      cacheKeys.forEach(key => {
        localStorage.removeItem(key);
      });
    } catch {
      // Ignore errors
    }
  }
}

// Create default cache instance
export const defaultCache = new CacheManager();

// Create cache instances for different use cases
export const analyticsCache = new CacheManager({
  ttl: 4 * 60 * 1000, // 4 minutes (slightly less than materialized view refresh)
  useLocalStorage: false, // Analytics data is large, keep in memory only
});

export const staticDataCache = new CacheManager({
  ttl: 30 * 60 * 1000, // 30 minutes for static data
  useLocalStorage: true, // Persist static data
  localStoragePrefix: 'static_',
});

/**
 * Stale-while-revalidate pattern
 * Returns cached data immediately and fetches fresh data in background
 */
export async function staleWhileRevalidate<T>(
  key: string,
  fetchFn: () => Promise<T>,
  cache: CacheManager = defaultCache,
  ttl?: number
): Promise<T> {
  // Return stale data immediately if available
  const cached = cache.get<T>(key);
  if (cached !== null) {
    // Fetch fresh data in background (don't await)
    fetchFn()
      .then(data => cache.set(key, data, ttl))
      .catch(error => console.warn('Background fetch failed:', error));
    return cached;
  }

  // No cache, fetch and cache
  const data = await fetchFn();
  cache.set(key, data, ttl);
  return data;
}

